import {nanoid} from 'nanoid';
import React, {useEffect, useRef, useState} from 'react';
import {
  PaymentWidgetInstance,
  loadPaymentWidget,
} from '@tosspayments/payment-widget-sdk';
import {useQuery} from '@tanstack/react-query';
import DefaultButton from './DefaultButton';
import {colors} from 'styles/colors';
import {ISubscriptionProduct} from 'interfaces/interface';
const selector = '#payment-widget';

// TODO: clientKey는 개발자센터의 결제위젯 연동 키 > 클라이언트 키로 바꾸세요.
// TODO: customerKey는 구매자와 1:1 관계로 무작위한 고유값을 생성하세요.
// @docs https://docs.tosspayments.com/reference/widget-sdk#sdk-설치-및-초기화
const clientKey = 'test_gck_docs_Ovk5rk1EwkEbP0W43n07xlzm';
const customerKey = nanoid();

/**
 * 토스 결제 컴포넌트의 Props 인터페이스
 * @property {number} totalAmount - 총 결제 금액
 * @property {ISubscriptionProduct | undefined} basicPlan - 기본 플랜 정보
 */
interface IProps {
  totalAmount: number;
  basicPlan: ISubscriptionProduct | undefined;
}

/**
 * 토스 결제 컴포넌트
 * 토스페이먼츠를 사용한 결제 처리를 담당하는 컴포넌트입니다.
 * @param {IProps} props - 토스 결제 컴포넌트의 props
 * @returns {JSX.Element} 토스 결제 컴포넌트
 */
export default function TossPayment({totalAmount, basicPlan}: IProps) {
  const {data: paymentWidget} = usePaymentWidget(clientKey, customerKey);
  const paymentMethodsWidgetRef = useRef<ReturnType<
    PaymentWidgetInstance['renderPaymentMethods']
  > | null>(null);
  const [price, setPrice] = useState(totalAmount);

  useEffect(() => {
    if (paymentWidget == null) {
      return;
    }

    // ------  결제 UI 렌더링 ------
    // @docs https://docs.tosspayments.com/reference/widget-sdk#renderpaymentmethods선택자-결제-금액-옵션
    const paymentMethodsWidget = paymentWidget.renderPaymentMethods(
      selector,
      {value: price},
      {variantKey: 'DEFAULT'},
    );

    // ------  이용약관 UI 렌더링 ------
    // @docs https://docs.tosspayments.com/reference/widget-sdk#renderagreement선택자-옵션
    paymentWidget.renderAgreement('#agreement', {variantKey: 'AGREEMENT'});

    paymentMethodsWidgetRef.current = paymentMethodsWidget;
  }, [paymentWidget]);

  useEffect(() => {
    const paymentMethodsWidget = paymentMethodsWidgetRef.current;

    if (paymentMethodsWidget == null) {
      return;
    }

    // ------ 금액 업데이트 ------
    // @docs https://docs.tosspayments.com/reference/widget-sdk#updateamount결제-금액
    paymentMethodsWidget.updateAmount(price);
  }, [price]);
  return (
    <div className="wrapper">
      <div className="box_section">
        <div id="payment-widget" />
        <div id="agreement" />
        <div style={{paddingLeft: '24px'}}></div>
        <div className="result wrapper">
          <DefaultButton
            text={'makePayment'}
            buttonStyle={{
              width: '100%',
              backgroundColor: colors.primary2,
            }}
            textStyle={{
              color: colors.white,
            }}
            onClick={async () => {
              // TODO: 결제를 요청하기 전에 orderId, amount를 서버에 저장하세요.
              // 결제 과정에서 악의적으로 결제 금액이 바뀌는 것을 확인하는 용도입니다.
              try {
                // ------ '결제하기' 버튼 누르면 결제창 띄우기 ------
                // @docs https://docs.tosspayments.com/reference/widget-sdk#requestpayment결제-정보
                await paymentWidget?.requestPayment({
                  orderId: nanoid(),
                  orderName: basicPlan ? basicPlan.name : '',
                  successUrl: `${window.location.origin}/successPage`,
                  failUrl: `${window.location.origin}/fail`,
                });
              } catch (error) {
                // 에러 처리하기
              }
            }}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * 결제 위젯 훅
 * 토스페이먼츠 결제 위젯을 초기화하는 커스텀 훅입니다.
 * @param {string} clientKey - 클라이언트 키
 * @param {string} customerKey - 고객 키
 * @returns {UseQueryResult<PaymentWidgetInstance, Error>} 결제 위젯 인스턴스
 */
function usePaymentWidget(clientKey: string, customerKey: string) {
  return useQuery({
    queryKey: ['payment-widget', clientKey, customerKey],
    queryFn: () => {
      // ------  결제위젯 초기화 ------
      // @docs https://docs.tosspayments.com/reference/widget-sdk#sdk-설치-및-초기화
      return loadPaymentWidget(clientKey, customerKey);
    },
  });
}
